// Option type representing an optional value.
// The 'Option' type can be either 'some' (contains a value) or 'none' (no value).
export type Option<T> = { kind: 'none'; } | { kind: 'some'; value: T; };

// Function to create a 'some' value, wrapping a given value in the Option type.
export function some<T>(value: T): Option<T> {
  return { kind: 'some', value: value };
}

// Example usage:
// const someValue = some(10); // someValue is now: { kind: 'some', value: 10 }

// None value representing the absence of any value.
export const none: Option<never> = { kind: 'none' };

// Example usage:
// const noValue = none; // noValue is now: { kind: 'none' }

// Check if the given Option is 'none'.
export function isNone<T>(option: Option<T>): boolean {
  return option.kind === 'none';
}

// Example usage:
// const result = isNone(none); // result is true
// const result2 = isNone(some(10)); // result2 is false

// Check if the given Option is 'some'.
export function isSome<T>(option: Option<T>): option is { kind: 'some'; value: T; } {
  return option.kind === 'some';
}

// Example usage:
// const result = isSome(some(10)); // result is true
// const result2 = isSome(none); // result2 is false

// Match function: Given an Option, apply the correct function depending on whether it's 'some' or 'none'.
// - onNone is called if the option is 'none'.
// - onSome is called if the option is 'some' and passed the value contained in the Option.
export function match<T, R>(onNone: () => R, onSome: (value: T) => R): (option: Option<T>) => R {
  return (option) => {
    switch (option.kind) {
      case 'none':
        return onNone();
      case 'some':
        return onSome(option.value);
    }
  };
}

// Example usage:
// const result = match(() => 'No value', (val) => `Value is ${val}`)(some(10)); 
// // result is "Value is 10"

// const result2 = match(() => 'No value', (val) => `Value is ${val}`)(none); 
// // result2 is "No value"

// Match function for two Options: Calls 'onBothSome' if both options are 'some'.
// Calls 'onEitherNone' if either option is 'none'.
export function match2<T1, T2, R>(
  onEitherNone: () => R,
  onBothSome: (value1: T1, value2: T2) => R
): (option1: Option<T1>, option2: Option<T2>) => R {
  return (option1, option2) => {
    if (isSome(option1) && isSome(option2)) {
      return onBothSome(option1.value, option2.value);
    } else {
      return onEitherNone();
    }
  };
}

// Example usage:
// const result = match2(() => 'No value', (v1, v2) => v1 + v2)(some(5), some(10)); 
// // result is 15

// const result2 = match2(() => 'No value', (v1, v2) => v1 + v2)(some(5), none); 
// // result2 is "No value"

// Map function for an Option: Applies a function to the value inside the Option if it's 'some'.
// Returns 'none' if the Option is 'none'.
export function map<T, R>(fn: (value: T) => R): (option: Option<T>) => Option<R> {
  return (option) => {
    if (isSome(option)) {
      return { kind: 'some', value: fn(option.value) };
    } else {
      return { kind: 'none' };
    }
  };
}

// Example usage:
// const result = map((x) => x * 2)(some(10)); 
// // result is { kind: 'some', value: 20 }

// const result2 = map((x) => x * 2)(none); 
// // result2 is { kind: 'none' }

// Map2 function: Applies a function to two Option values if both are 'some'.
// Returns 'none' if either Option is 'none'.
export function map2<T1, T2, R>(fn: (value1: T1, value2: T2) => R): (option1: Option<T1>, option2: Option<T2>) => Option<R> {
  return (option1, option2) => {
    if (isSome(option1) && isSome(option2)) {
      return some(fn(option1.value, option2.value));
    } else {
      return none;
    }
  };
}

// Example usage:
// const result = map2((x, y) => x + y)(some(5), some(10)); 
// // result is { kind: 'some', value: 15 }

// const result2 = map2((x, y) => x + y)(some(5), none); 
// // result2 is { kind: 'none' }

// Map3 function: Similar to map2, but for three Option values.
// Returns 'some' if all three options are 'some', otherwise returns 'none'.
export function map3<T1, T2, T3, R>(fn: (value1: T1, value2: T2, value3: T3) => R): (option1: Option<T1>, option2: Option<T2>, option3: Option<T3>) => Option<R> {
  return (option1, option2, option3) => {
    if (isSome(option1) && isSome(option2) && isSome(option3)) {
      return some(fn(option1.value, option2.value, option3.value));
    } else {
      return none;
    }
  };
}

// Example usage:
// const result = map3((x, y, z) => x + y + z)(some(5), some(10), some(15)); 
// // result is { kind: 'some', value: 30 }

// const result2 = map3((x, y, z) => x + y + z)(some(5), none, some(15)); 
// // result2 is { kind: 'none' }

// MapIfNone function: If the Option is 'none', applies a function to produce a new value wrapped in 'some'.
// If the Option is already 'some', it returns the Option unchanged.
export function mapIfNone<T>(fn: () => T): (option: Option<T>) => Option<T> {
  return (option) => {
    if (isNone(option)) {
      return some(fn());
    } else {
      return option;
    }
  };
}

// Example usage:
// const result = mapIfNone(() => 42)(none); 
// // result is { kind: 'some', value: 42 }

// const result2 = mapIfNone(() => 42)(some(10)); 
// // result2 is { kind: 'some', value: 10 }

// Bind function: Similar to map, but the function returns an Option itself, allowing for chaining.
export function bind<T, R>(fn: (value: T) => Option<R>): (option: Option<T>) => Option<R> {
  return (option) => {
    if (isSome(option)) {
      return fn(option.value);
    } else {
      return none;
    }
  };
}

// Example usage:
// const result = bind((x) => some(x * 2))(some(10)); 
// // result is { kind: 'some', value: 20 }

// const result2 = bind((x) => some(x * 2))(none); 
// // result2 is { kind: 'none' }

// Fold function: Unpacks the Option, applying one function if 'none' and another if 'some'.
export function fold<T, R>(onNone: () => R, onSome: (value: T) => R): (option: Option<T>) => R {
  return (option) => {
    switch (option.kind) {
      case 'none':
        return onNone();
      case 'some':
        return onSome(option.value);
    }
  };
}

// Example usage:
// const result = fold(() => 'No value', (val) => `Value is ${val}`)(some(10)); 
// // result is "Value is 10"

// const result2 = fold(() => 'No value', (val) => `Value is ${val}`)(none); 
// // result2 is "No value"

// Get the value from an Option, returning a default value if 'none'.
export function getOrElse<T>(defaultValue: () => T): (option: Option<T>) => T {
  return (option) => {
    switch (option.kind) {
      case 'none':
        return defaultValue();
      case 'some':
        return option.value;
    }
  };
}

// Example usage:
// const result = getOrElse(() => 42)(some(10)); 
// // result is 10

// const result2 = getOrElse(() => 42)(none); 
// // result2 is 42

// Pipe function: Passes an initial value through a series of functions.
export function pipe<T>(initialValue: T, ...fns: Array<(arg: any) => any>): any {
  return fns.reduce((currentValue, currentFunction) => currentFunction(currentValue), initialValue);
}

// Example usage:
// const result = pipe(5, (x) => x + 2, (x) => x * 2); 
// // result is 14

// Tee function: Applies a side-effect function to the value if 'some', but always returns the original Option.
export function tee<T>(sideEffectFn: (value: T) => void): (option: Option<T>) => Option<T> {
  return (option) => {
    if (isSome(option)) {
      sideEffectFn(option.value);
    }
    return option;
  };
}

// Example usage:
// const result = tee((x) => console.log(x))(some(10)); 
// // Logs: 10
// // result is { kind: 'some', value: 10 }

// Tap function: Similar to tee, applies a side-effect function to the value if 'some', but always returns the original Option.
export function tap<T>(sideEffectFn: (value: T) => void): (option: Option<T>) => Option<T> {
  return (option) => {
    if (isSome(option)) {
      sideEffectFn(option.value);
    }
    return option;
  };
}

// Example usage:
// const result = tap((x) => console.log(x))(some(10));
// // Logs: 10
// // result is { kind: 'some', value: 10 }

// Converts a potentially undefined or null value to an Option.
// If the value is null or undefined, it returns 'none'.
// Otherwise, it wraps the value in a 'some'.
export function fromNullable<T>(value: T | null | undefined): Option<T> {
  return value != null ? some(value) : none;
}

// Example usage:
//const result1 = fromNullable(10); // result1 is { kind: 'some', value: 10 }
//const result2 = fromNullable(null); // result2 is { kind: 'none' }
//const result3 = fromNullable(undefined); // result3 is { kind: 'none' }


// Unwrap the value from 'some', throws if 'none'
export function unwrap<T>(option: Option<T>): T {
  if (isSome(option)) {
    return option.value;
  }
  throw new Error('Cannot unwrap a none value');
}

// Safe unwrap with default value
export function unwrapOr<T>(defaultValue: T): (option: Option<T>) => T {
  return (option) => isSome(option) ? option.value : defaultValue;
}



